import { Injectable } from '@angular/core';

/**
 * Service for tracking and formatting changes made to services
 * Manages change history arrays and formats change notes with timestamps
 * 
 * Category: Helper Service
 * Purpose: Change tracking, note formatting, timestamp management
 */
@Injectable()
export class ChangeTrackerService {

  constructor() { }

  /**
   * Formats a change entry with timestamp
   * @param field - Field name that changed
   * @param value - New value
   * @param additionalInfo - Optional additional context
   * @returns Formatted change string
   */
  formatChange(field: string, value: any, additionalInfo?: string): string {
    const timestamp = this.getTimestamp();
    const info = additionalInfo ? ` ${additionalInfo}` : '';
    return `${field}${info}: ${value}-${timestamp}`;
  }

  /**
   * Adds a change to the changes array
   * @param changes - Array of changes
   * @param field - Field that changed
   * @param value - New value
   * @param additionalInfo - Optional additional context
   */
  addChange(changes: string[], field: string, value: any, additionalInfo?: string): void {
    const changeString = this.formatChange(field, value, additionalInfo);
    changes.push(changeString);
  }

  /**
   * Joins changes array into a single note string
   * @param changes - Array of change strings
   * @param prefix - Optional prefix to add
   * @returns Joined change notes
   */
  joinChanges(changes: string[], prefix: string = '- '): string {
    if (changes.length === 0) {
      return '';
    }
    return prefix + changes.join(', ') + ', ';
  }

  /**
   * Clears the changes array
   * @param changes - Array to clear
   */
  clearChanges(changes: string[]): void {
    changes.length = 0;
  }

  /**
   * Gets current timestamp formatted for change notes
   * @returns Formatted timestamp string
   */
  private getTimestamp(): string {
    const now = new Date();
    return `${now.toDateString()} ${now.toLocaleTimeString()}`;
  }

  /**
   * Formats multiple changes into a single note object
   * @param changes - Array of changes
   * @param servicioId - Service ID
   * @param userId - User making changes
   * @param additionalNote - Optional additional note
   * @returns Formatted note object
   */
  createNoteObject(
    changes: string[],
    servicioId: string,
    userId: string,
    additionalNote?: string
  ): any {
    const changeNote = this.joinChanges(changes);
    const finalNote = additionalNote ? `${changeNote}Nota:${additionalNote}` : changeNote;

    return {
      nota: finalNote,
      servicio: servicioId,
      fecha: Date.now(),
      usuario: userId
    };
  }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/helpers/change-tracker.service.ts